import React, { PureComponent } from 'react';
import * as PropTypes from 'prop-types';
import get from 'lodash.get';
import Icon from '@ifit/react-icons/dist/components/icon';
import CHEVRON_DOWN_LINE from '@ifit/react-icons/dist/icons/CHEVRON_DOWN_LINE';
import CHECKBOX_SOLID from '@ifit/react-icons/dist/icons/CHECKBOX_SOLID';
import CHECKBOX_EMPTY_LINE from '@ifit/react-icons/dist/icons/CHECKBOX_EMPTY_LINE';
import Loading from '@ifit/mp-loading';
import {
  shark80,
  scienceBlue,
  mountainMeadow,
  solidWhite,
  selectBorder,
} from '../../../../theme';
import classnames from 'classnames';
import './index.scss';
import { withTheme } from '@ifit/themes/withTheme';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import {
  clearFilterButtonText,
  closeFilterButtonText,
} from '../../../../i18n/translations';

class FilterDropdown extends PureComponent {
  state = {
    dropDownOpen: false,
  };

  static contextType = ThemeContext;

  onDropdownSelect = () => {
    this.setState({ dropDownOpen: !this.state.dropDownOpen }, () => {
      if (this.state.dropDownOpen && this.props.onDropdownSelect) {
        this.props.onDropdownSelect(this.node);
      }
    });
  };

  componentDidMount() {
    document.addEventListener('click', this.handleClick, false);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClick, false);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.filtersOpen !== this.props.filtersOpen &&
      !this.props.filtersOpen
    ) {
      this.setState({ dropDownOpen: false });
    }
  }

  handleClick = event => {
    if (
      this.node.contains(event.target) &&
      event.target.value !== 'close-and-hide'
    ) {
      return;
    }
    if (this.props.filtersOpen) {
      this.handleClickOutside();
    }
  };

  handleClickOutside = () => this.setState({ dropDownOpen: false });

  onFilterCheck = event =>
    this.props.onFilterCheck(this.props.dataLabel, event.target.value);

  checkFilters = () => {
    const { selected } = this.props;
    return Boolean(selected && selected.length);
  };

  render() {
    const {
      label,
      pluralLabel,
      options,
      selected = [],
      filtersLoaded,
      filtersOpen,
      isMobile,
    } = this.props;

    const { theme } = this.context;
    const iconSize = 20;
    const chevronColor = {
      LIGHT: shark80,
      DARK: solidWhite,
      VAULT: shark80,
    }[theme];

    function themeColor(optionValue) {
      const optionSelected = !optionValue
        ? !selected || selected.length === 0
        : selected && selected.includes(optionValue);
      return {
        LIGHT: optionSelected ? scienceBlue : shark80,
        DARK: optionSelected ? mountainMeadow : selectBorder,
        VAULT: optionSelected ? scienceBlue : shark80,
      }[theme];
    }

    const elementBottom =
      this.dropdownRef &&
      window.innerHeight -
        (this.dropdownRef.offsetTop + this.dropdownRef.offsetHeight) -
        80;

    const elementFromTop = this.props.isVault
      ? this.node && this.node.offsetTop + this.dropdownRef.offsetTop + 150
      : this.node && this.node.offsetTop + this.dropdownRef.offsetTop;

    const [singleValue] = selected;
    const getOption =
      singleValue &&
      get(
        options.find(option => option.value === singleValue),
        'display',
        undefined,
      );
    const englishCheck =
      label === 'Language' && getOption === 'English' && selected.length === 1;

    const displayLabel = (() => {
      return (
        <React.Fragment>
          <span className="filter-title" title={label}>
            {label}
          </span>
          {selected.length && !englishCheck ? (
            <span className="filter-number">({selected.length})</span>
          ) : (
            <span className="filter-number"></span>
          )}
        </React.Fragment>
      );
    })();

    const checkBoxSelect =
      !selected || !selected.length ? CHECKBOX_SOLID : CHECKBOX_EMPTY_LINE;

    return (
      <div
        className={classnames(
          `filter-dropdown-component ${this.props.themeClass}`,
          {
            'mobile-animate': filtersOpen,
            'small-search-experience': isMobile,
          },
        )}
        ref={node => (this.node = node)}
      >
        <div
          className={classnames('dropdown-label', {
            'filter-added': this.checkFilters(),
            'blue-border': this.state.dropDownOpen && !isMobile,
          })}
          data-label={label}
          onClick={this.onDropdownSelect}
        >
          <div className="dropdown-label-text">{displayLabel}</div>
          {this.state.dropDownOpen ? (
            filtersLoaded ? (
              <Icon
                icon={CHEVRON_DOWN_LINE}
                color={chevronColor}
                size={iconSize}
                unitSize="px"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  transformOrigin: '50% 50%',
                  transform: 'rotate(180deg)',
                  WebkitTransform: 'rotate(180deg)',
                }}
              />
            ) : (
              <div className="filters-loading">
                <Loading absolute />
              </div>
            )
          ) : (
            <Icon
              icon={CHEVRON_DOWN_LINE}
              color={chevronColor}
              size={iconSize}
              unitSize="px"
            />
          )}
        </div>
        <div
          ref={node => (this.dropdownRef = node)}
          className={classnames('filter-dropdown', {
            'cat-dropdownopen': this.state.dropDownOpen,
          })}
          style={{
            maxHeight: !isMobile ? `calc(100vh - ${elementFromTop}px)` : 'null',
          }}
        >
          <div
            className={classnames('scroll-inside', {
              'at-bottom': !isMobile && elementBottom,
            })}
            style={{
              maxHeight: !isMobile
                ? `calc(100vh - ${elementFromTop}px)`
                : 'null',
            }}
          >
            {filtersLoaded && (
              <div className="filters">
                <div
                  className={classnames('filter', {
                    'filter-selected': !selected.length,
                  })}
                >
                  <label htmlFor={label} className="filter-label">
                    <Icon icon={checkBoxSelect} color={themeColor()} />
                    <span title={pluralLabel}>{pluralLabel}</span>
                  </label>
                  <input
                    id={label}
                    type="checkbox"
                    value="alloptions"
                    onChange={this.onFilterCheck}
                    checked={!selected.length}
                  />
                </div>
                {options.map(option => (
                  <div
                    className={classnames('filter', {
                      'filter-selected':
                        selected && selected.includes(option.value),
                    })}
                    data-testid={`filter-${option.value}`}
                    key={`${option.value}${option.display}`}
                  >
                    <label
                      htmlFor={`${label}-${option.value}`}
                      className="filter-label"
                    >
                      <Icon
                        icon={
                          selected && selected.includes(option.value)
                            ? CHECKBOX_SOLID
                            : CHECKBOX_EMPTY_LINE
                        }
                        color={themeColor(option.value)}
                      />
                      {option.display}
                    </label>
                    <input
                      id={`${label}-${option.value}`}
                      checked={selected && selected.includes(option.value)}
                      type="checkbox"
                      value={option.value}
                      onChange={this.onFilterCheck}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          {!isMobile && filtersLoaded && (
            <div
              className={classnames('clear-and-close', {
                'fixed-apply-clear': elementBottom,
              })}
            >
              <button
                className={classnames('filter-clear', {
                  'clear-highlight': this.checkFilters(),
                })}
                value="alloptions"
                onClick={this.onFilterCheck}
              >
                {clearFilterButtonText()}
              </button>
              <button
                className="close-and-hide"
                value="close-and-hide"
                onClick={this.handleClickOutside}
              >
                {closeFilterButtonText()}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

FilterDropdown.propTypes = {
  dataLabel: PropTypes.string,
  label: PropTypes.string,
  pluralLabel: PropTypes.string,
  onDropdownSelect: PropTypes.func,
  onFilterCheck: PropTypes.func,
  options: PropTypes.array,
  selections: PropTypes.array,
  selected: PropTypes.array,
  filtersOpen: PropTypes.bool,
  filtersLoaded: PropTypes.bool,
  themeClass: PropTypes.string,
  isMobile: PropTypes.bool,
};

export default withTheme(FilterDropdown);
